
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * BeginReadSnapshotResult struct
 */
class BeginReadSnapshotResult {
  /**
   * Copy struct (shallow copy)
   * @this {!BeginReadSnapshotResult}
   * @param {!BeginReadSnapshotResult} other Other struct
   * @returns {!BeginReadSnapshotResult} This struct
   */
  copy (other) {
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BeginReadSnapshotResult}
   * @returns {!BeginReadSnapshotResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BeginReadSnapshotResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BeginReadSnapshotResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BeginReadSnapshotResult}
   * @param {!BeginReadSnapshotResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BeginReadSnapshotResult)) {
      throw new TypeError('Instance of BeginReadSnapshotResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BeginReadSnapshotResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BeginReadSnapshotResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BeginReadSnapshotResult} other Object value
   * @returns {!BeginReadSnapshotResult} Created struct
   */
  static fromObject (other) {
    return new BeginReadSnapshotResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BeginReadSnapshotResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BeginReadSnapshotResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BeginReadSnapshotResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4213
  }
}

export { BeginReadSnapshotResult };

/**
 * Fast Binary Encoding BeginReadSnapshotResult field model
 */
class FieldModelBeginReadSnapshotResult extends fbe.FieldModel {
  /**
   * Get the field size
   * @this {!FieldModelBeginReadSnapshotResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBeginReadSnapshotResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBeginReadSnapshotResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBeginReadSnapshotResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBeginReadSnapshotResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBeginReadSnapshotResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4213
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBeginReadSnapshotResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {!BeginReadSnapshotResult} fbeValue Default value, defaults is new BeginReadSnapshotResult()
   * @returns {!BeginReadSnapshotResult} BeginReadSnapshotResult value
   */
  get (fbeValue = new BeginReadSnapshotResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {!BeginReadSnapshotResult} fbeValue BeginReadSnapshotResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBeginReadSnapshotResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {!BeginReadSnapshotResult} fbeValue BeginReadSnapshotResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBeginReadSnapshotResult}
   * @param {BeginReadSnapshotResult} fbeValue BeginReadSnapshotResult value
   */
  setFields (fbeValue) {
  }
}

export { FieldModelBeginReadSnapshotResult };

/**
 * Fast Binary Encoding BeginReadSnapshotResult model
 */
class BeginReadSnapshotResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBeginReadSnapshotResult(this.buffer, 4)
  }

  /**
   * Get the BeginReadSnapshotResult model
   * @this {!BeginReadSnapshotResultModel}
   * @returns {!FieldModelBeginReadSnapshotResult} model BeginReadSnapshotResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BeginReadSnapshotResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BeginReadSnapshotResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BeginReadSnapshotResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BeginReadSnapshotResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBeginReadSnapshotResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BeginReadSnapshotResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BeginReadSnapshotResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BeginReadSnapshotResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BeginReadSnapshotResultModel}
   * @param {!BeginReadSnapshotResult} value BeginReadSnapshotResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BeginReadSnapshotResultModel}
   * @param {!BeginReadSnapshotResult} value BeginReadSnapshotResult value, defaults is new BeginReadSnapshotResult()
   * @return {!object} Deserialized BeginReadSnapshotResult value and its size
   */
  deserialize (value = new BeginReadSnapshotResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BeginReadSnapshotResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BeginReadSnapshotResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BeginReadSnapshotResultModel}
   * @param {!number} prev Previous BeginReadSnapshotResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BeginReadSnapshotResultModel };
